import dayjs, { Dayjs } from 'dayjs';

//dát to work-schedule profile
export type WorkScheduleMonth = {
  id: string;
  userId: string;
  year: number;
  month: number;
  schedule: MonthSchedule;
  createdAt: string;
  updatedAt: string;
};

// -________

export type TimeSlot = {
  start: string;
  end: string;
};

export type NoSlotsReason = 'NOT_WORKING' | 'FULLY_BOOKED';

export type AvailableSlotsResult = {
  slots: TimeSlot[];
  reason?: NoSlotsReason;
};

export type DaySchedule = {
  isWorking: boolean;
  timeSlots: TimeSlot[];
};

export type DayAvailability = 'morning' | 'afternoon' | 'both' | null;

export type MonthSchedule = {
  weeklySchedule: WeeklySchedule;
  holidays: Holiday[];
  dayOverrides?: Record<string, DaySchedule>;
};

export type WeeklySchedule = {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
};

export type WeekDays = keyof WeeklySchedule;

export type Holiday = {
  date: string;
  name?: string;
  startTime?: string; // Format: "HH:mm" - if not specified, whole day is blocked
  endTime?: string; // Format: "HH:mm" - if not specified, whole day is blocked
};

export type WorkingHoursScheduleType = {
  weeklySchedule: WeeklySchedule;
  holidays: Holiday[];
  dayOverrides?: Record<string, DaySchedule>;
  monthlyWeeklySchedules?: Record<string, WeeklySchedule>;
};

export const DAYS_OF_WEEK = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

export type DayOfWeek = (typeof DAYS_OF_WEEK)[number];

export const DEFAULT_DAY_SCHEDULE: DaySchedule = {
  isWorking: false,
  timeSlots: [],
};

export const DEFAULT_WEEKLY_SCHEDULE: WeeklySchedule = {
  monday: { isWorking: true, timeSlots: [{ start: '09:00', end: '17:00' }] },
  tuesday: { isWorking: true, timeSlots: [{ start: '09:00', end: '17:00' }] },
  wednesday: { isWorking: true, timeSlots: [{ start: '09:00', end: '17:00' }] },
  thursday: { isWorking: true, timeSlots: [{ start: '09:00', end: '17:00' }] },
  friday: { isWorking: true, timeSlots: [{ start: '09:00', end: '17:00' }] },
  saturday: { isWorking: false, timeSlots: [] },
  sunday: { isWorking: false, timeSlots: [] },
};

export const getWorkScheduleForDateRange = (
  startMonthSchedule: WorkScheduleMonth | undefined,
  endMonthSchedule: WorkScheduleMonth | undefined,
): WorkingHoursScheduleType | undefined => {
  const {
    holidays: startMonthHolidays = [],
    weeklySchedule: startMonthWeeklySchedule,
    dayOverrides: startMonthDayOverrides,
  } = startMonthSchedule?.schedule ?? {};

  const { holidays: endMonthHolidays = [], dayOverrides: endMonthDayOverrides } =
    endMonthSchedule?.schedule ?? {};

  if (!startMonthSchedule || !startMonthWeeklySchedule) {
    return undefined;
  }
  const holidaysUnique = [...startMonthHolidays, ...endMonthHolidays].filter(
    (holiday1, index, holidayArr) =>
      holidayArr.findIndex(holiday2 => holiday2.date === holiday1.date) === index,
  );

  const dayOverrides = { ...startMonthDayOverrides, ...endMonthDayOverrides };

  return { weeklySchedule: startMonthWeeklySchedule, holidays: holidaysUnique, dayOverrides };
};

/** Lists every calendar month (inclusive) spanned by the given date range. */
export const getMonthsInRange = (from: Dayjs, to: Dayjs): { year: number; month: number }[] => {
  const months: { year: number; month: number }[] = [];
  let cursor = from.startOf('month');
  const end = to.startOf('month');

  while (cursor.valueOf() <= end.valueOf()) {
    months.push({ year: cursor.year(), month: cursor.month() + 1 });
    cursor = cursor.add(1, 'month');
  }

  return months;
};

/**
 * Merges the schedules of all months in a date range into a single working
 * schedule. Holidays and day overrides from every month are combined so that
 * vacations entered in any month within the range propagate to the overview.
 */
export const mergeWorkSchedules = (
  schedules: (WorkScheduleMonth | undefined)[],
): WorkingHoursScheduleType | undefined => {
  const available = schedules.filter((schedule): schedule is WorkScheduleMonth =>
    Boolean(schedule?.schedule?.weeklySchedule),
  );

  if (available.length === 0) {
    return undefined;
  }

  const weeklySchedule = available[0].schedule.weeklySchedule;

  const holidays = available
    .flatMap(schedule => schedule.schedule.holidays ?? [])
    .filter((holiday, index, arr) => arr.findIndex(h => h.date === holiday.date) === index);

  const dayOverrides = available.reduce<Record<string, DaySchedule>>(
    (acc, schedule) => ({ ...acc, ...(schedule.schedule.dayOverrides ?? {}) }),
    {},
  );

  const monthlyWeeklySchedules = available.reduce<Record<string, WeeklySchedule>>(
    (acc, schedule) => {
      const key = `${schedule.year}-${String(schedule.month).padStart(2, '0')}`;
      acc[key] = schedule.schedule.weeklySchedule;
      return acc;
    },
    {},
  );

  return { weeklySchedule, holidays, dayOverrides, monthlyWeeklySchedules };
};

export const isDateHoliday = (targetDate: Dayjs, workingSchedule: WorkingHoursScheduleType) => {
  if (!workingSchedule?.holidays) {
    return false;
  }

  return workingSchedule.holidays.some(holiday => {
    const holidayDate = dayjs(holiday.date);
    const isMatchingDate = holidayDate.format('YYYY-MM-DD') === targetDate.format('YYYY-MM-DD');

    return isMatchingDate && (!holiday.startTime || !holiday.endTime);
  });
};

export const getDayScheduleLabel = (day: Dayjs, workSchedule: WorkingHoursScheduleType): string => {
  const dayStr = day.format('YYYY-MM-DD');

  // 1. Full-day holiday
  const holiday = workSchedule.holidays?.find(
    h => dayjs(h.date).format('YYYY-MM-DD') === dayStr && !h.startTime && !h.endTime,
  );
  if (holiday) {
    return ` | Dovolená${holiday.name ? ': ' + holiday.name : ''}`;
  }

  // 2. Day override
  const override = workSchedule.dayOverrides?.[dayStr];
  if (override) {
    if (!override.isWorking || override.timeSlots.length === 0) {
      return ' | Nepracovní (upraveno)';
    }
    return ' | ' + override.timeSlots.map(s => `${s.start}–${s.end}`).join(', ') + ' (upraveno)';
  }

  // 3. Regular weekly schedule — use the schedule for the specific month if available
  const monthKey = day.format('YYYY-MM');
  const weeklySchedule =
    workSchedule.monthlyWeeklySchedules?.[monthKey] ?? workSchedule.weeklySchedule;
  const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][
    day.day()
  ] as keyof WeeklySchedule;
  const daySchedule = weeklySchedule[dayOfWeek];
  if (!daySchedule?.isWorking || daySchedule.timeSlots.length === 0) {
    return ' | Nepracovní';
  }
  return ' | ' + daySchedule.timeSlots.map(s => `${s.start}–${s.end}`).join(', ');
};

export const isWorkingDay = (targetDate: Dayjs, workingSchedule: WorkingHoursScheduleType) => {
  if (!workingSchedule?.weeklySchedule) {
    return false;
  }

  const dayStr = targetDate.format('YYYY-MM-DD');
  const override = workingSchedule.dayOverrides?.[dayStr];
  if (override) {
    return override.isWorking && override.timeSlots?.length > 0;
  }

  const monthKey = targetDate.format('YYYY-MM');
  const weeklySchedule =
    workingSchedule.monthlyWeeklySchedules?.[monthKey] ?? workingSchedule.weeklySchedule;

  const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][
    targetDate.day()
  ] as keyof WeeklySchedule;

  const daySchedule = weeklySchedule[dayOfWeek];
  return daySchedule?.isWorking && daySchedule.timeSlots?.length > 0;
};
